import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { AppShell } from "../components/layout/AppShell";
import { MessageBubble, TypingBubble } from "../components/chat/MessageBubble";
import { ChatInput } from "../components/chat/ChatInput";
import { GPTIcon } from "../components/GPTIcon";
import { ArrowLeft, Plus, MessageSquare, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Chat() {
  const { gptId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = params.get("session");

  // Hydrate GPT from navigation state when available — avoids the empty-state fetch flicker
  const [gpt, setGpt] = useState(location.state?.gpt || null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const scrollerRef = useRef(null);
  const sendingRef = useRef(false); // re-entry lock — prevents double-fires from rapid clicks
  const skipNextLoadRef = useRef(false); // suppress the messages fetch when we just created a session ourselves
  // Load GPT info
  useEffect(() => {
    if (!gptId) return;
    api
      .get(`/gpts/${gptId}`)
      .then(({ data }) => setGpt(data))
      .catch(() => {
        toast.error("GPT not found");
        navigate("/");
      });
  }, [gptId, navigate]);

  // Load sessions list for this GPT
  const loadSessions = useCallback(async () => {
    if (!gptId) return;
    try {
      const { data } = await api.get(`/chat/sessions`, { params: { gpt_id: gptId } });
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load chat sessions:", err);
    }
  }, [gptId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load messages when session changes
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    // If we just created this session ourselves (via send), skip the load —
    // handleSend's response will populate messages and avoid the race
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    setLoadingMessages(true);
    api
      .get(`/chat/sessions/${sessionId}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {
        toast.error("Could not load chat");
        setMessages([]);
      })
      .finally(() => setLoadingMessages(false));
  }, [sessionId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const { data } = await api.post("/chat/sessions", { gpt_id: gptId });
    skipNextLoadRef.current = true; // suppress the next messages-load fetch — we'll populate ourselves
    setParams({ session: data.id }, { replace: true });
    setSessions((prev) => [data, ...prev]);
    return data.id;
  };

  const handleSend = async (overrideContent = null) => {
    // Synchronous re-entry lock — protects against React 19 event re-fires & rapid double-clicks
    if (sendingRef.current) return;
    const content = (overrideContent ?? input).toString();
    if (!content.trim() || !gpt) return;
    sendingRef.current = true;
    if (!overrideContent) setInput("");
    setSending(true);

    // Optimistic user bubble
    const tempId = "temp-" + Date.now();
    setMessages((prev) => [
      ...prev,
      { id: tempId, role: "user", content, created_at: new Date().toISOString() },
    ]);

    try {
      const sid = await ensureSession();
      const { data } = await api.post(`/chat/sessions/${sid}/messages`, { content });
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempId),
        data.user_message,
        data.assistant_message,
      ]);
      // Update session title in list
      setSessions((prev) => {
        const idx = prev.findIndex((s) => s.id === sid);
        if (idx === -1) return [data.session, ...prev];
        const copy = [...prev];
        copy[idx] = data.session;
        return [copy[idx], ...copy.filter((_, i) => i !== idx)];
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message failed");
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const newChat = () => {
    setParams({}, { replace: true });
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.delete(`/chat/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (sid === sessionId) newChat();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <AppShell>
      <Toaster position="top-center" richColors />
      <div className="flex h-[calc(100dvh-4rem)] md:h-[calc(100dvh-5rem)]">
        {/* Sessions rail (desktop) */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-border/60 bg-background/60">
          <div className="px-5 pt-7 pb-3">
            <button
              data-testid="chat-back"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={14} /> Back to Hub
            </button>
          </div>
          <div className="px-4">
            <button
              data-testid="new-chat-button"
              onClick={newChat}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm flex items-center justify-center gap-2 hover:opacity-90 transition"
            >
              <Plus size={15} /> New chat
            </button>
          </div>
          <div className="px-3 mt-4 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            History
          </div>
          <div className="flex-1 overflow-y-auto thin-scroll px-3 mt-2 space-y-1 pb-6">
            {sessions.length === 0 && (
              <div className="text-xs text-muted-foreground px-3 py-4">
                No chats yet.
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                data-testid={`session-item-${s.id}`}
                onClick={() => setParams({ session: s.id }, { replace: true })}
                className={[
                  "group w-full text-left px-3 py-2.5 rounded-xl flex items-start gap-2 transition-colors",
                  s.id === sessionId
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                ].join(" ")}
              >
                <MessageSquare size={14} className="mt-1 shrink-0" strokeWidth={1.6} />
                <span className="flex-1 text-sm truncate">{s.title}</span>
                <span
                  role="button"
                  onClick={(e) => deleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-background"
                  aria-label="Delete chat"
                >
                  <Trash2 size={13} strokeWidth={1.6} />
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header */}
          <div className="sticky top-0 z-30 backdrop-blur-xl bg-background/80 border-b border-border/60">
            <div className="max-w-4xl mx-auto w-full px-4 md:px-8 py-3 flex items-center gap-3">
              <button
                data-testid="chat-back-mobile"
                onClick={() => navigate("/")}
                className="lg:hidden h-9 w-9 rounded-full border border-border/60 flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Back"
              >
                <ArrowLeft size={16} />
              </button>
              <div className="h-10 w-10 rounded-xl bg-secondary border border-border/60 flex items-center justify-center">
                <GPTIcon name={gpt?.icon || "Bot"} size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-base md:text-lg tracking-tight truncate">
                  {gpt?.name || "Chat"}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {gpt?.description}
                </div>
              </div>
              <button
                data-testid="new-chat-button-header"
                onClick={newChat}
                className="ml-auto hidden md:inline-flex h-9 px-3.5 rounded-full border border-border/60 items-center gap-1.5 text-sm hover:bg-secondary transition-colors"
              >
                <Plus size={14} /> New
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto thin-scroll"
            data-testid="chat-messages"
          >
            <div className="max-w-3xl mx-auto w-full px-4 md:px-6 py-6 md:py-10 space-y-5 pb-40">
              {loadingMessages ? (
                <div className="text-center text-muted-foreground text-sm pt-10">
                  Loading chat…
                </div>
              ) : messages.length === 0 ? (
                <EmptyState gpt={gpt} onStart={(content) => handleSend(content)} sending={sending} />
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    gptIcon={gpt?.icon || "Bot"}
                  />
                ))
              )}
              {sending && <TypingBubble gptIcon={gpt?.icon || "Bot"} />}
            </div>
          </div>

          {/* Input — fixed bottom */}
          <div className="sticky bottom-0 z-20 bg-gradient-to-t from-background via-background/90 to-transparent pb-4 md:pb-8 pt-3 px-4">
            <ChatInput
              value={input}
              onChange={setInput}
              onSend={handleSend}
              disabled={sending || (gpt && gpt.locked)}
              placeholder={
                gpt?.locked
                  ? "This GPT is locked by admin"
                  : `Ask ${gpt?.name || "the GPT"}…`
              }
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function EmptyState({ gpt, onStart, sending }) {
  const fallbackSuggestions = {
    "conversion-leak-detector": [
      "My checkout drops 52% at address step — diagnose it",
      "Our landing page CTR is 1.8%. Find the leaks.",
    ],
    "ugc-ad-creator": [
      "Write 3 UGC hooks for a sleep supplement",
      "Creator brief for a skincare brand targeting Gen Z",
    ],
    "delivery-rate-optimizer": [
      "Our RTO is 32% on COD — give me a 30-day plan",
      "Reduce undelivered tier-3 city orders",
    ],
    "full-business-automation-architect": [
      "Automate my Shopify → WhatsApp → CRM flow",
      "Design an end-to-end lead nurturing system",
    ],
  };
  // Prefer the GPT's own conversation_starters array from the API; fall back
  // to the local map for the legacy GPTs that don't yet have one in the DB.
  const items =
    (gpt?.conversation_starters && gpt.conversation_starters.length > 0
      ? gpt.conversation_starters
      : fallbackSuggestions[gpt?.slug]) || [];
  const starter = gpt?.conversation_starter;
  return (
    <div
      data-testid="chat-empty-state"
      className="flex flex-col items-center text-center py-10 md:py-16 animate-fade-up"
    >
      <div className="h-16 w-16 rounded-2xl bg-card border border-border/60 flex items-center justify-center shadow-subtle">
        <GPTIcon name={gpt?.icon || "Bot"} size={28} />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold mt-4 tracking-tight">
        {gpt?.name || "Start a conversation"}
      </h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md">
        {gpt?.description}
      </p>

      {starter && (
        <button
          type="button"
          data-testid="conversation-starter-button"
          disabled={sending || gpt?.locked}
          onClick={() => onStart && onStart(starter)}
          className="mt-7 h-12 px-8 rounded-full bg-brand text-brand-foreground font-medium tracking-wide inline-flex items-center gap-2 hover:opacity-90 transition disabled:opacity-50 shadow-[0_8px_24px_-6px_hsl(var(--brand-accent)/0.55)]"
        >
          {starter}
          <span aria-hidden>→</span>
        </button>
      )}

      {items.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
          {items.map((s, i) => (
            <button
              key={s}
              type="button"
              data-testid={`suggestion-${i}`}
              disabled={sending}
              onClick={() => onStart && onStart(s)}
              className="text-left bg-card border border-border/60 rounded-2xl px-4 py-3 text-sm text-foreground/90 shadow-subtle hover:-translate-y-0.5 hover:shadow-hover transition-all disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
